/**
 * The signed log scale (`AxisInfo.scale: "signedLog"`, owner 2026-10-29).
 *
 * A growth rate with no cap — EPS YoY off a one-cent base reaches thousands of
 * percent — on a linear axis presses every other reading flat onto zero, and a
 * plain log axis cannot hold zero or a fall. This is the usual answer:
 *
 * ```
 * y = sign(v) · log10(1 + |v| / SIGNED_LOG_KNEE)
 * ```
 *
 * so 10% → 100% → 1,000% take near-equal steps, mirrored below zero, and the
 * stretch under the knee is close to linear. Values are fractions (1 = 100%).
 *
 * **The renderer draws `y`, the reader sees `v`.** The DataSeries, the Y fit
 * and every annotation on the axis work in `y`; the axis labels and the
 * last-value tag invert it. The value legend and the data panel read the
 * table, never the series, so they show the true number without help.
 */

/** Where the scale turns from near-linear to logarithmic: 10%. */
export const SIGNED_LOG_KNEE = 0.1;

/** A value as drawn on a signed log axis. Non-finite passes through. */
export function toSignedLog(v: number): number {
  if (!Number.isFinite(v)) return v;
  return Math.sign(v) * Math.log10(1 + Math.abs(v) / SIGNED_LOG_KNEE);
}

/** The true value at a drawn position — the inverse of {@link toSignedLog}. */
export function fromSignedLog(y: number): number {
  if (!Number.isFinite(y)) return y;
  return Math.sign(y) * (Math.pow(10, Math.abs(y)) - 1) * SIGNED_LOG_KNEE;
}

/**
 * Major ticks for a drawn range `[min, max]`, as drawn positions.
 *
 * At zero and at ±10%, ±100%, ±1,000% …: the decades are what the scale makes
 * equal, so they are what a reader counts. When fewer than four decades fall in
 * view — a pane zoomed into one stretch — the 2× and 5× steps between them are
 * added, so a short axis still has a scale to read.
 */
export function signedLogTicks(min: number, max: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  const lo = fromSignedLog(min);
  const hi = fromSignedLog(max);
  const top = Math.max(Math.abs(lo), Math.abs(hi));
  const steps = (mantissas: number[]) => {
    const out = new Set<number>([0]);
    for (let p = SIGNED_LOG_KNEE / 10; p <= top * 10; p *= 10) {
      for (const m of mantissas) {
        const v = Number((m * p).toPrecision(6));
        out.add(v);
        out.add(-v);
      }
    }
    return [...out].filter((v) => v >= lo && v <= hi).sort((a, b) => a - b);
  };
  let values = steps([1]).filter((v) => v === 0 || Math.abs(v) >= SIGNED_LOG_KNEE);
  if (values.length < 4) values = steps([1, 2, 5]);
  return values.map(toSignedLog);
}

/**
 * A drawn position as its true value, in percent: `+40%`, `−25%`, `1,200%`,
 * `28K%`. Precision follows the size, because a scale spanning 3% and 3,000%
 * has no single right number of decimals.
 */
export function signedLogLabel(y: number): string {
  const v = fromSignedLog(y) * 100;
  if (!Number.isFinite(v)) return "";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : v > 0 ? "+" : "";
  let body: string;
  if (abs >= 1e6) body = `${trim(abs / 1e6)}M`;
  else if (abs >= 1e4) body = `${trim(abs / 1e3)}K`;
  else if (abs >= 100) body = Math.round(abs).toLocaleString("en-US");
  else if (abs >= 10) body = abs.toFixed(0);
  else if (abs >= 1) body = abs.toFixed(1).replace(/\.0$/, "");
  else body = abs === 0 ? "0" : abs.toFixed(2).replace(/0$/, "");
  return `${sign}${body}%`;
}

function trim(n: number): string {
  return n >= 100 ? n.toFixed(0) : n.toFixed(1).replace(/\.0$/, "");
}
